<template lang="pug">
.question-block(v-if="questionBlock.text")
  .question-container
    .question-content
      .question-text {{ questionBlock.text }}

      .answer-input-wrapper
        el-input(
          v-model="answer"
          :placeholder="'Type your answer...'"
          :disabled="isSubmitting"
          @keyup.enter="handleSubmit"
          @blur="handleInputBlur"
          clearable
          ref="inputRef"
        )
          template(#suffix)
            .send-button(
              @click="handleSubmit"
              :class="{ 'active': canSubmit, 'disabled': !canSubmit }"
            )
              img(src="@/assets/icons/send.svg" alt="Submit" v-if="canSubmit")
              img(src="@/assets/icons/send-disabled.svg" alt="Submit" v-else)

      .sponsored-info(v-if="questionBlock.organization")
        .org-avatar(v-if="questionBlock.organization.avatar")
          img(:src="questionBlock.organization.avatar" :alt="questionBlock.organization.name")
        .sponsored-text Sponsored by {{ questionBlock.organization.name }}
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, PropType,
} from 'vue';
import { ElMessage } from 'element-plus';
import webAppToast from '@/utils/webAppToast';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { QuestionBlock as QuestionBlockType } from '../types';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { useStaticPage } from '../useStaticPage';

export default defineComponent({
  name: 'QuestionBlock',
  props: {
    block: {
      type: Object as PropType<QuestionBlockType>,
      required: true,
    },
  },
  setup(props) {
    const { submitAnswer } = useStaticPage();

    // State
    const answer = ref('');
    const isSubmitting = ref(false);
    const inputRef = ref();

    // Computed
    const questionBlock = computed(() => props.block as QuestionBlockType);

    const canSubmit = computed(() => answer.value.trim().length > 0 && !isSubmitting.value);

    // Methods
    const handleInputBlur = () => {
      // Fix iOS keyboard issue where page gets stuck after keyboard dismissal
      // Force the page to scroll back to proper position
      setTimeout(() => {
        window.scrollTo({
          top: window.scrollY,
          left: 0,
          behavior: 'instant',
        });
        // Trigger a resize event to force layout recalculation
        window.dispatchEvent(new Event('resize'));
      }, 100);
    };

    const handleSubmit = async () => {
      if (!canSubmit.value) return;

      const trimmedAnswer = answer.value.trim();
      if (!trimmedAnswer) return;

      const { questionId } = questionBlock.value;
      if (!questionId) {
        ElMessage.error('Question ID is missing');
        return;
      }

      isSubmitting.value = true;

      try {
        const success = await submitAnswer(questionId, trimmedAnswer);

        if (success) {
          webAppToast.success('Answer submitted successfully');
          answer.value = '';

          // Unfocus the input
          if (inputRef.value) {
            inputRef.value.blur();
          }
        } else {
          ElMessage.error('Failed to submit answer. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting answer:', error);
        ElMessage.error('Failed to submit answer. Please try again.');
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      questionBlock,
      answer,
      isSubmitting,
      canSubmit,
      inputRef,
      handleSubmit,
      handleInputBlur,
    };
  },
});
</script>

<style lang="scss" scoped>
.question-block {
  margin-bottom: 48px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 56px;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 64px;
  }
}

.question-container {
  width: 100%;
  max-width: 800px;
}

.question-content {
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #225c6b 100%);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(13, 148, 136, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 50%,
      rgba(0, 0, 0, 0.1) 100%
    );
    pointer-events: none;
  }

  @media (min-width: 768px) {
    padding: 32px;
  }
}

.question-text {
  color: white;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;

  @media (min-width: 768px) {
    font-size: 24px;
  }
}

.answer-input-wrapper {
  margin-bottom: 0;
  position: relative;
  z-index: 1;

  :deep(.el-input) {
    .el-input__wrapper {
      background-color: white;
      border-radius: 12px;
      box-shadow: none;
      border: none;
      padding: 14px 16px;
      min-height: 48px;

      &:hover,
      &:focus,
      &.is-focus {
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
      }
    }

    .el-input__inner {
      color: #1a1a1a;
      font-size: 16px;
      font-weight: 500;

      &::placeholder {
        color: #9ca3af;
      }
    }

    .el-input__suffix {
      display: flex;
      align-items: center;
      padding-right: 8px;
    }
  }
}

.send-button {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: all 0.2s;

  &.active {
    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);

    img {
      opacity: 1;
      filter: brightness(0) invert(1);
    }

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
    }
  }

  &.disabled {
    cursor: not-allowed;
    background: #f3f4f6;

    img {
      opacity: 0.3;
      filter: grayscale(100%);
    }
  }

  img {
    width: 18px;
    height: 18px;
  }
}

.sponsored-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.org-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.sponsored-text {
  color: white;
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
}
</style>
